import React from 'react';
import { Tick02Icon } from 'hugeicons-react';
import {
    PricingTier,
    Currency,
    Interval,
    formatMoney,
    monthlyEquivalent,
    annualSavingsPercent,
    CURRENCY_LABEL,
} from '../../Data/PricingData';
import { Tier } from '../../types/user';
import '../../Styles/Pricing/PricingCard.css';

interface PricingCardProps {
    tier: PricingTier;
    billingCycle: Interval;
    currency: Currency;
    /** Viewer's plan: null when signed out, 'free' when signed in without a subscription. */
    currentPlan: Tier | null;
    /** Signed-in viewer is still on their trial. */
    isTrialing?: boolean;
    pending: boolean;
    disabled?: boolean;
    onSelect: (tier: Tier) => void;
}

const TIER_RANK: Record<Tier, number> = { free: 0, premium: 1, ultimate: 2 };

const PricingCard: React.FC<PricingCardProps> = ({
    tier,
    billingCycle,
    currency,
    currentPlan,
    isTrialing,
    pending,
    disabled,
    onSelect,
}) => {
    const isFree = tier.idname === 'free';
    const isCurrent = currentPlan === tier.idname;
    const price = tier.price[currency];
    const perMonth = monthlyEquivalent(tier, currency, billingCycle);
    const savings = annualSavingsPercent(tier, currency);
    const offersTrial = Boolean(tier.trialDays) && (currentPlan === null || currentPlan === 'free');

    let cta: string;
    let note: string | null = null;
    if (isCurrent) {
        cta = isFree ? 'Your Current Plan' : 'Manage Plan';
        if (!isFree) note = isTrialing ? 'You are on your free trial.' : null;
    } else if (currentPlan && currentPlan !== 'free') {
        cta = TIER_RANK[tier.idname] > TIER_RANK[currentPlan] ? `Upgrade to ${tier.name}` : `Switch to ${tier.name}`;
        note = 'Changes are made in the billing portal.';
    } else if (isFree) {
        cta = 'Get Started';
        note = 'No card required.';
    } else if (offersTrial) {
        cta = `Start ${tier.trialDays}-Day Free Trial`;
        const after = billingCycle === 'annual'
            ? `${formatMoney(price.annual, currency)}/year`
            : `${formatMoney(price.monthly, currency)}/month`;
        note = `Then ${after}. Cancel anytime.`;
    } else {
        cta = `Get ${tier.name}`;
    }

    const ctaDisabled = disabled || pending || (isCurrent && isFree);

    return (
        <article
            className={`pricing-card ${tier.highlight ? 'is-highlighted' : ''} ${isCurrent ? 'is-current' : ''}`}
            aria-labelledby={`plan-${tier.idname}-name`}
        >
            <header className="pricing-card-header">
                <div className="pricing-card-title-row">
                    <h3 id={`plan-${tier.idname}-name`}>{tier.name}</h3>
                    {isCurrent ? (
                        <span className="pricing-card-badge is-current">Current Plan</span>
                    ) : tier.highlight ? (
                        <span className="pricing-card-badge">Recommended</span>
                    ) : null}
                </div>
                <p className="pricing-card-tagline">{tier.tagline}</p>
            </header>

            <div className="pricing-card-price">
                <div className="pricing-card-amount">
                    <span className="pricing-card-value">
                        {isFree ? formatMoney(0, currency).replace(/\.00$/, '') : formatMoney(perMonth, currency)}
                    </span>
                    <span className="pricing-card-period">
                        {CURRENCY_LABEL[currency]} / month
                    </span>
                </div>
                <p className="pricing-card-billing">
                    {isFree ? (
                        'Free Forever'
                    ) : billingCycle === 'annual' ? (
                        <>
                            {formatMoney(price.annual, currency)} Billed Yearly
                            {savings > 0 && <span className="pricing-card-save">Save {savings}%</span>}
                        </>
                    ) : (
                        'Billed Monthly'
                    )}
                </p>
            </div>

            <div className="pricing-card-action">
                <button
                    type="button"
                    className={`pricing-button ${tier.highlight && !isCurrent ? 'is-primary' : ''}`}
                    onClick={() => onSelect(tier.idname)}
                    disabled={ctaDisabled}
                    aria-busy={pending}
                >
                    {pending ? <span className="pricing-button-spinner" aria-hidden /> : null}
                    <span>{pending ? 'Opening…' : cta}</span>
                </button>
                <p className="pricing-card-note">{note ?? ' '}</p>
            </div>

            <ul className="pricing-card-features">
                {tier.features.map(feature => (
                    <li key={feature}>
                        <Tick02Icon size={16} aria-hidden />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
        </article>
    );
};

export default PricingCard;
